/**
 * bridge/barcode_scanner_controller.js — "barcode-scanner" bridge component.
 *
 * Capability-tier bridge component: opens a native camera scanner for QR codes and
 * 1D/2D barcodes (referral QR, material SKUs) and returns the decoded string to the
 * web layer via callback. Stimulus identifier "bridge--barcode-scanner"
 * (glob-derived from this subdir); Strada component name "barcode-scanner" MUST
 * match the iOS class var name (native/ios/Chippie/Bridge/BarcodeScannerComponent.swift)
 * and the Android-reached method name.
 *
 * Why it extends Strada's BridgeComponent: the iOS scanner is reached via
 * this.send("scan", ...), which only works on a connected bridge component inside
 * the iOS Hotwire Native shell. Extending BridgeComponent gives us that send()
 * transport AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport: on connect this controller registers a single scan() impl
 * set onto the window.ChippieBridge facade ({ ios, android, pwa }). The facade
 * (chippie-bridge.js) owns platform detection and picks the right impl per call.
 * Because scanning is asynchronous, scan() takes a node-style callback
 * (err, result) where result is { value, symbology }.
 *   - ios:     this.send("scan", {}, reply) -> BarcodeScannerComponent.swift; the
 *              reply payload is { value, symbology } | { cancelled } | { error }.
 *   - android: window.ChippieNative.scanBarcode() launches CameraX + ML Kit; the
 *              native side resolves asynchronously by calling back into
 *              window.ChippieBridge._onScanResult(json) (see registration below),
 *              which routes to the pending callback.
 *   - pwa:     no-op (returns an "unsupported" error). A pure-DOM scanner would
 *              need a third-party library; out of scope for this component.
 *
 * Template wiring:
 *   <button data-controller="bridge--barcode-scanner"
 *           data-action="bridge--barcode-scanner#scan">Scan</button>
 * Or call window.ChippieBridge.scan((err, result) => { ... }) directly from any JS.
 *
 * The Android impl and its result router carry no per-instance state, so they are
 * installed at module load via registerStandalone() and ChippieBridge.scan()
 * resolves everywhere. Only the ios impl needs a live Strada instance for
 * this.send, so connect() overrides it and disconnect() reverts to the
 * "unsupported" placeholder. Registering solely on connect (as this file used to)
 * left ChippieBridge.scan UNDEFINED on Android and PWA as well as everywhere the
 * UA-gated BridgeComponent.connect() never fires — callers got a TypeError rather
 * than the documented (err, result) callback.
 *
 * Web side adapted from joemasilotti/bridge-components barcode_scanner_controller.js.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

function invoke(callback, err, result) {
  if (typeof callback === "function") callback(err, result);
}

// Shared decoder for both the iOS reply and the Android router: the two native
// sides emit the same payload shape, so the branch order lives in one place.
function deliver(callback, data) {
  data = data || {};
  if (data.error) return invoke(callback, new Error(data.error));
  if (data.cancelled) return invoke(callback, null, null);
  invoke(callback, null, { value: data.value, symbology: data.symbology });
}

function androidScan(callback) {
  if (!window.ChippieNative || !window.ChippieNative.scanBarcode) {
    return invoke(callback, new Error("unsupported"));
  }
  // Native resolves asynchronously by calling _onScanResult(json); stash the
  // callback so that router can find it.
  window.ChippieBridge._pendingScan = callback;
  try {
    window.ChippieNative.scanBarcode();
  } catch (_e) {
    window.ChippieBridge._pendingScan = null;
    invoke(callback, new Error("unsupported"));
  }
}

// No pure-DOM scanner: that would need a third-party decoding library, which is
// out of scope for this component.
function pwaScan(callback) {
  invoke(callback, new Error("unsupported"));
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;

  window.ChippieBridge.register("scan", {
    // ios is overridden by the controller's connect() with a real send; this
    // placeholder reports unsupported rather than hanging the callback.
    ios: (callback) => invoke(callback, new Error("unsupported")),
    android: androidScan,
    pwa: pwaScan,
  });

  // Android result router: native calls window.ChippieBridge._onScanResult(json)
  // from the WebView (evaluateJavascript) once scanning resolves. The argument is
  // a JSON string: { value, symbology } | { cancelled: true } | { error }.
  window.ChippieBridge._onScanResult = (json) => {
    const callback = window.ChippieBridge._pendingScan;
    window.ChippieBridge._pendingScan = null;
    if (!callback) return;
    let data = {};
    try { data = JSON.parse(json) || {}; } catch (_e) { /* malformed */ }
    deliver(callback, data);
  };
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "barcode-scanner";

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    registerStandalone();
    window.ChippieBridge.register("scan", {
      ios: (callback) => {
        this.send("scan", {}, (message) => deliver(callback, message && message.data));
      },
      android: androidScan,
      pwa: pwaScan,
    });
  }

  disconnect() {
    // Revert the live-send ios impl to the standalone placeholder. Deleting the
    // facade method and nulling the router (the old behaviour) also took the
    // capability away from Android and PWA, which never needed this instance.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--barcode-scanner#scan" trigger. Dispatches a
  // "barcode-scanner:result" CustomEvent on this element so a host page can react
  // without owning the callback wiring; on error it dispatches "barcode-scanner:error".
  scan(event) {
    if (event) event.preventDefault();
    window.ChippieBridge.scan((err, result) => {
      if (err) {
        this.dispatch("error", { detail: { error: err.message } });
        return;
      }
      if (!result) return; // cancelled
      this.dispatch("result", { detail: result });
    });
  }
}
